import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import * as r from "constants/routes";
import { addNotificationMessages, logout } from "../slices/appStateSlice";
import * as dates from "commons/models/dates";

const setHeaders = (headers, token) => {
  headers.set("Content-Type", "application/json");
  headers.set("Authorization", token);
  return headers;
};

const baseQueryInterceptor = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  // @ts-ignore
  if (result.data?.erreur && result.data?.erreur?.length > 0) {
    // @ts-ignore
    switch (result.data.erreur[0].numero) {
      case 401:
      case 403:
      default:
        api.dispatch(logout());
        break;
    }
  } else if (result.data?.success === false) {
    if (api.type === "mutation") {
      const { method } = args;
      switch (method) {
        case "PUT":
          api.dispatch(
            addNotificationMessages({
              text: "Erreur lors de la modification, réessayer!",
              style: "error",
              id: Math.random(),
              timeout: 3000,
            })
          );
          break;
        case "DELETE":
          api.dispatch(
            addNotificationMessages({
              text: "Erreur lors de la suppression, réessayer!",
              style: "error",
              id: Math.random(),
              timeout: 3000,
            })
          );
          break;
        case "POST":
          if (api.endpoint !== "login") {
            api.dispatch(
              addNotificationMessages({
                text: "Erreur lors de l'ajout, réessayer!",
                style: "error",
                id: Math.random(),
                timeout: 3000,
              })
            );
          }
          break;
        default:
          break;
      }
    }
  } else {
    if (api.type === "mutation") {
      const { method } = args;
      switch (method) {
        case "PUT":
          api.dispatch(
            addNotificationMessages({
              text: "La modification a été enregistré avec succès.",
              style: "success",
              id: Math.random(),
              timeout: 3000,
            })
          );
          break;
        case "DELETE":
          api.dispatch(
            addNotificationMessages({
              text: "La suppression a été enregistré avec succès.",
              style: "success",
              id: Math.random(),
              timeout: 3000,
            })
          );
          break;
        case "POST":
          if (api.endpoint !== "login") {
            api.dispatch(
              addNotificationMessages({
                text: "L'ajout a été enregistré avec succès.",
                style: "success",
                id: Math.random(),
                timeout: 3000,
              })
            );
          }
          break;
        default:
          break;
      }
    }
  }

  return result;
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,
  prepareHeaders: (headers, { getState }) => {
    // @ts-ignore
    return setHeaders(headers, getState().appState.token);
  },
});

export const pubApi = createApi({
  reducerPath: "pubApi",
  baseQuery: baseQueryInterceptor,
  tagTypes: [
    "campagneReq",
    "project",
    "campagneDiffuseur",
    "documentsMeta",
    "campagneNote",
    "campagneTypeProd",
    "annonceProd",
    "documents",
    "taches",
    "tachesPlanif",
    "annonces",
    "annoncesVigueur",
    "url",
    "tachesUsager",
  ],
  endpoints: (builder) => ({
    getTaches: builder.query({
      query: ({ usr_cd, filtre }) =>
        `${r.TACHE}${getUrlParams({ usr_cd, filtre })}`,
      providesTags: ["taches"],
    }),
    getTachesPlanif: builder.query({
      query: ({ tac_id, usr_cd }) =>
        `${r.TACHE_PLANIF}${getUrlParams({ tac_id, usr_cd })}`,
      providesTags: ["tachesPlanif"],
    }),
    getTachesUsager: builder.query({
      query: ({ tac_id, usr_cd }) =>
        `${r.TACHE_USAGER}${getUrlParams({ tac_id, usr_cd })}`,
      providesTags: ["tachesUsager"],
    }),
    getInventaire: builder.query({
      query: ({ query }) => `${r.INVENTAIRES}?${query}`,
    }),
    getTypeEnvoiInstructions: builder.query({
      query: () => `${r.TYPE_ENVOIE_INSTRUCTIONS}`,
    }),
    getDocTypeMeta: builder.query({
      query: () => `${r.DOC_TYPE_META}`,
    }),
    getDocMeta: builder.query({
      query: (rid_doc) => `${r.DOCUMENTS_META}${getUrlParams({ rid_doc })}`,
      providesTags: ["documentsMeta"],
    }),
    getListingProduits: builder.query({
      query: (params) =>
        params ? `${r.LISTING_PRODUITS}?${params}` : `${r.LISTING_PRODUITS}`,
    }),
    getFournisseurs: builder.query({
      query: (aveccd) => `${r.FOURNISSEURS}${getUrlParams({ aveccd })}`,
    }),
    getAnnoncePlan: builder.query({
      query: () => `${r.ANNONCE_PLAN}`,
    }),
    getAnnonceProd: builder.query({
      query: (ann_seq) => `${r.ANNONCE_PROD}${getUrlParams({ ann_seq })}`,
      providesTags: ["annonceProd"],
    }),
    getAnnoncesVigueur: builder.query({
      query: ({ ann_seq, seq, camp_seq, media }) =>
        `${r.ANNONCES_VIGUEURS}${getUrlParams({
          ann_seq,
          seq,
          camp_seq,
          media,
        })}`,
      providesTags: ["annoncesVigueur"],
    }),
    getListUsagers: builder.query({
      query: ({ type, usr, aveccd }) =>
        `${r.LISTE_USAGER}${getUrlParams({ type, usr, aveccd })}`,
    }),
    getBannieres: builder.query({
      query: () => `${r.BANNIERES}`,
    }),
    getObjectif: builder.query({
      query: () => `${r.OBJECTIF}`,
    }),
    getProject: builder.query({
      query: ({ seq, camp_num, camp_descr, query }) =>
        query
          ? `${r.PROJECT}?${query}`
          : `${r.PROJECT}${getUrlParams({ seq, camp_num, camp_descr })}`,
      providesTags: ["project"],
    }),
    getReseaux: builder.query({
      query: () => `${r.RESEAUX}`,
    }),
    getDiffuseurs: builder.query({
      query: (media_cd) => `${r.DIFFUSEURS}${getUrlParams({ media_cd })}`,
    }),
    getAmbiance: builder.query({
      query: () => `${r.AMBIANCE}`,
    }),
    getMedias: builder.query({
      query: () => `${r.MEDIA}`,
    }),
    getCampagneTypes: builder.query({
      query: () => `${r.CAMPAGNE_TYPES}`,
    }),
    getMagasins: builder.query({
      query: () => `${r.MAGASINS}`,
    }),
    getCampagneReq: builder.query({
      query: ({ seq, media, date, diff, them_cd, mf_format }) =>
        `${r.CAMPAGNE_REQ}${getUrlParams({
          seq,
          media,
          date,
          diff,
          them_cd,
          mf_format,
        })}`,
      providesTags: ["campagneReq"],
      transformResponse: (response) => {
        // @ts-ignore
        response.data.forEach((e) => (e.inEdit = false));
        return response;
      },
    }),
    getDivisions: builder.query({
      query: () => `${r.DIVISIONS}`,
    }),
    getDepartements: builder.query({
      query: ({ aveccd, div }) =>
        `${r.DEPARTEMENT}${getUrlParams({ aveccd, div })}`,
    }),
    getCategories: builder.query({
      query: (aveccd) => `${r.CATEGORIE}${getUrlParams({ aveccd })}`,
    }),
    getFormatsPage: builder.query({
      query: ({ media, diff, format }) =>
        `${r.FORMAT_PAGE}${getUrlParams({ media, diff, format })}`,
    }),
    getCampagneMedia: builder.query({
      query: (seq) => `${r.CAMPAGNE_MEDIA}${getUrlParams({ seq })}`,
    }),
    getCampagneDiffuseur: builder.query({
      query: ({ seq, media }) =>
        `${r.CAMPAGNE_DIFFUSEUR}${getUrlParams({ seq, media })}`,
      providesTags: ["campagneDiffuseur"],
    }),
    getDocuments: builder.query({
      query: ({ seq, ann_seq, media, params }) =>
        `${r.DOCUMENTS}${getUrlParams({ seq, ann_seq, media })}&${
          params ? params : ""
        }`,
      providesTags: ["documents"],
    }),
    getCampagneNotes: builder.query({
      query: ({ seq, media, params }) =>
        `${r.CAMPAGNE_NOTE}${getUrlParams({ seq, media })}&${
          params ? params : ""
        }`,
      providesTags: ["campagneNote"],
    }),
    getCampagneTypeProd: builder.query({
      query: ({ camp_seq, media_cd, diff_cd, div_num, campr_date, them_cd }) =>
        `${r.CAMPAGNE_TYPE_PROD}${getUrlParams({
          camp_seq,
          media_cd,
          diff_cd,
          div_num,
          campr_date,
          them_cd,
        })}`,
      providesTags: ["campagneTypeProd"],
    }),
    getInstructions: builder.mutation({
      query: ({ seq, media, date, diff, mf_format }) => ({
        url: `${r.INSTRUCTIONS}${getUrlParams({
          seq,
          media,
          date,
          diff,
          mf_format,
        })}`,
        method: "GET",
      }),
      invalidatesTags: ["campagneReq"],
    }),
    getAnnonces: builder.query({
      query: ({ ann_num, query }) =>
        query
          ? `${r.ANNONCES}?${query}`
          : `${r.ANNONCES}${getUrlParams({ ann_num })}`,
      providesTags: ["annonces"],
    }),
    getUrl: builder.query({
      query: (query) => query,
      providesTags: ["url"],
    }),
    getUrlWithParams: builder.query({
      query: ({ query, params }) => `${query}?${params}`,
      providesTags: ["url"],
    }),
    getTreq: builder.query({
      query: (camp_seq) => ({
        url: `${r.TREQ}${getUrlParams({ camp_seq })}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    getThematique: builder.query({
      query: () => r.THEMATIQUE,
    }),
    getRegistreApprobation: builder.query({
      query: (camp_seq) =>
        `${r.REGISTRE_APPROBATION}${getUrlParams({ camp_seq })}`,
    }),
    getAchatsMedias: builder.query({
      query: (params) =>
        params ? `${r.ACHATS_MEDIAS}?${params}` : `${r.ACHATS_MEDIAS}`,
      transformResponse: (response) => {
        // @ts-ignore
        let retour = response.data.map((elem) => {
          return {
            ...elem,
            campc_dt: dates.dateQueryFormatReverse(elem.campc_dt),
            campc_dt_fin: dates.dateQueryFormatReverse(elem.campc_dt_fin),
          };
        });
        return { ...response, data: retour };
      },
    }),
    getRapportCoop: builder.query({
      query: (params) =>
        params ? `${r.RAPPORT_COOP}?${params}` : `${r.RAPPORT_COOP}`,
      transformResponse: (response) => {
        // @ts-ignore
        let retour = response.data.map((elem) => {
          return {
            ...elem,
            camp_dt_deb: dates.dateQueryFormatReverse(elem.camp_dt_deb),
            camp_dt_fin: dates.dateQueryFormatReverse(elem.camp_dt_fin),
          };
        });
        return { ...response, data: retour };
      },
    }),
    addProject: builder.mutation({
      query: (body) => ({
        url: r.PROJECT,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["project"],
    }),
    addCampagneReq: builder.mutation({
      query: (body) => ({
        url: r.CAMPAGNE_REQ,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["campagneReq"],
    }),
    addCampagneDiffuseur: builder.mutation({
      query: (body) => ({
        url: r.CAMPAGNE_DIFFUSEUR,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["campagneDiffuseur"],
    }),
    addDocumentsMeta: builder.mutation({
      query: (body) => ({
        url: r.DOCUMENTS_META,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["documentsMeta"],
    }),
    addCampagneNote: builder.mutation({
      query: (body) => ({
        url: r.CAMPAGNE_NOTE,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["campagneNote"],
    }),
    addCampagneTypeProd: builder.mutation({
      query: (body) => ({
        url: r.CAMPAGNE_TYPE_PROD,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["campagneTypeProd"],
    }),
    addAnnonceProd: builder.mutation({
      query: (body) => ({
        url: r.ANNONCE_PROD,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["annonceProd"],
    }),
    addDocument: builder.mutation({
      query: ({ update, body }) => ({
        url: `${r.HOT_FOLDER}${getUrlParams({ update })}`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["documents", "documentsMeta"],
    }),
    addTache: builder.mutation({
      query: (body) => ({
        url: r.TACHE,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["taches"],
    }),
    addTacheActivite: builder.mutation({
      query: (body) => ({
        url: r.TACHE_ACTIVITE,
        method: "POST",
        body: body,
      }),
    }),
    addTachePlanif: builder.mutation({
      query: (body) => ({
        url: r.TACHE_PLANIF,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["tachesPlanif"],
    }),
    addTacheUsager: builder.mutation({
      query: (body) => ({
        url: r.TACHE_USAGER,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["tachesUsager", "taches"],
    }),
    addAnnonce: builder.mutation({
      query: (body) => ({
        url: r.ANNONCES,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["annonces"],
    }),
    addAnnonceVigueur: builder.mutation({
      query: (body) => ({
        url: r.ANNONCES_VIGUEURS,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["annoncesVigueur"],
    }),
    postUrl: builder.mutation({
      query: ({ path, body }) => ({
        url: path,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["url"],
    }),
    addProduitAnnonce: builder.mutation({
      query: (body) => ({
        url: r.PRODUIT_ANNONCE,
        method: "POST",
        body: body,
        responseHandler: "text",
      }),
    }),
    updateProject: builder.mutation({
      query: (body) => ({
        url: r.PROJECT,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["project"],
    }),
    updateAnnoncesVigueur: builder.mutation({
      query: (body) => ({
        url: r.ANNONCES_VIGUEURS,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["annoncesVigueur"],
    }),
    updateCampagneReq: builder.mutation({
      query: (body) => ({
        url: r.CAMPAGNE_REQ,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["campagneReq"],
    }),
    updateCampagneDiffuseur: builder.mutation({
      query: (body) => ({
        url: r.CAMPAGNE_DIFFUSEUR,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["campagneDiffuseur"],
    }),
    updateDocumentsMeta: builder.mutation({
      query: (body) => ({
        url: r.DOCUMENTS_META,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["documentsMeta"],
    }),
    updateCampagneNote: builder.mutation({
      query: (body) => ({
        url: r.CAMPAGNE_NOTE,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["campagneNote"],
    }),
    updateCampagneTypeProd: builder.mutation({
      query: (body) => ({
        url: r.CAMPAGNE_TYPE_PROD,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["campagneTypeProd"],
    }),
    updateCampagneTypeProdAffiche: builder.mutation({
      query: (body) => ({
        url: r.CAMPAGNE_TYPE_PROD_AFFICHE,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["campagneTypeProd"],
    }),
    updateTachePlanif: builder.mutation({
      query: (body) => ({
        url: r.TACHE_PLANIF,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["tachesPlanif", "taches"],
    }),
    updateTacheUsager: builder.mutation({
      query: (body) => ({
        url: r.TACHE_USAGER,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["tachesUsager", "taches"],
    }),
    updateAnnonce: builder.mutation({
      query: (body) => ({
        url: r.ANNONCES,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["annonces"],
    }),
    putUrl: builder.mutation({
      query: ({ path, body }) => ({
        url: path,
        method: "PUT",
        body: body,
      }),
      invalidatesTags: ["url"],
    }),
    deleteURL: builder.mutation({
      query: ({ path, body }) => ({
        url: path,
        method: "DELETE",
        body: body,
      }),
      invalidatesTags: ["url"],
    }),
    removeProject: builder.mutation({
      query: (seq) => ({
        url: `${r.PROJECT}${getUrlParams({ seq })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["project"],
    }),
    removeCampagneReq: builder.mutation({
      query: (rid) => ({
        url: `${r.CAMPAGNE_REQ}${getUrlParams({ rid })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["campagneReq"],
    }),
    removeDocument: builder.mutation({
      query: (rid) => ({
        url: `${r.DOCUMENTS}${getUrlParams({ rid })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["documents"],
    }),
    removeCampagneDiffuseur: builder.mutation({
      query: ({ seq, media, rid }) => ({
        url: `${r.CAMPAGNE_DIFFUSEUR}${getUrlParams({ seq, media, rid })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["campagneDiffuseur"],
    }),
    removeDocumentMeta: builder.mutation({
      query: (rid) => ({
        url: `${r.DOCUMENTS_META}${getUrlParams({ rid })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["documentsMeta"],
    }),
    removeCampagneNote: builder.mutation({
      query: ({ seq, media, titre, campr_date, diff_cd, mf_format }) => ({
        url: `${r.CAMPAGNE_NOTE}${getUrlParams({
          seq,
          media,
          titre,
          campr_date,
          diff_cd,
          mf_format,
        })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["campagneNote"],
    }),
    removeCampagneTypeProd: builder.mutation({
      query: (rid) => ({
        url: `${r.CAMPAGNE_TYPE_PROD}${getUrlParams({ rid })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["campagneTypeProd"],
    }),
    removeAnnonceProd: builder.mutation({
      query: (rid) => ({
        url: `${r.ANNONCE_PROD}${getUrlParams({ rid })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["annonceProd"],
    }),
    removeTachePlanif: builder.mutation({
      query: (rid) => ({
        url: `${r.TACHE_PLANIF}${getUrlParams({ rid })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["tachesPlanif"],
    }),
    removeAnnonce: builder.mutation({
      query: ({ ann_seq, ann_num }) => ({
        url: `${r.ANNONCES}${getUrlParams({ ann_seq, ann_num })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["annonces"],
    }),
    removeAnnonceVigueur: builder.mutation({
      query: (rid) => ({
        url: `${r.ANNONCES_VIGUEURS}${getUrlParams({ rid })}`,
        method: "DELETE",
      }),
      invalidatesTags: ["annoncesVigueur"],
    }),
    addCampagneMedia: builder.mutation({
      query: (body) => ({
        url: `${r.CAMPAGNE_MEDIA}`,
        method: "POST",
        body: body,
      }),
    }),
    removeCampagneMedia: builder.mutation({
      query: ({ seq, media }) => ({
        url: `${r.CAMPAGNE_MEDIA}${getUrlParams({ seq, media })}`,
        method: "DELETE",
      }),
    }),
    loginWithCredentials: builder.mutation({
      query: (body) => ({
        url: `${process.env.REACT_APP_API_URL + r.LOG_IN}`,
        method: "POST",
        body: body,
      }),
    }),
  }),
});

const getUrlParams = (args) => {
  let parameters = "";
  let operateur = "?";
  Object.keys(args).forEach((arg) => {
    if (
      (args[arg] ||
        args[arg] === 0 ||
        (arg === "mf_format" && args[arg] === "")) &&
      args[arg] !== -1
    ) {
      parameters = parameters.concat(`${operateur}${arg}=${args[arg]}`);
      operateur = operateur === "?" ? "&" : operateur;
    }
  });

  return parameters;
};

export const {
  useGetTachesQuery,
  useGetTachesPlanifQuery,
  useGetTachesUsagerQuery,
  useGetInventaireQuery,
  useGetTypeEnvoiInstructionsQuery,
  useGetDocTypeMetaQuery,
  useGetDocMetaQuery,
  useGetListingProduitsQuery,
  useGetFournisseursQuery,
  useGetAnnoncePlanQuery,
  useGetAnnonceProdQuery,
  useGetAnnoncesVigueurQuery,
  useGetListUsagersQuery,
  useGetBannieresQuery,
  useGetObjectifQuery,
  useGetProjectQuery,
  useGetReseauxQuery,
  useGetDiffuseursQuery,
  useGetAmbianceQuery,
  useGetMediasQuery,
  useGetCampagneTypesQuery,
  useGetMagasinsQuery,
  useGetCampagneReqQuery,
  useGetDivisionsQuery,
  useGetDepartementsQuery,
  useGetCategoriesQuery,
  useGetFormatsPageQuery,
  useGetCampagneDiffuseurQuery,
  useGetDocumentsQuery,
  useGetCampagneNotesQuery,
  useGetCampagneTypeProdQuery,
  useGetAnnoncesQuery,
  useGetUrlWithParamsQuery,
  useGetUrlQuery,
  useGetThematiqueQuery,
  useGetRegistreApprobationQuery,
  useGetRapportCoopQuery,
  useGetAchatsMediasQuery,
  useLazyGetAchatsMediasQuery,
  useLazyGetTreqQuery,
  useLazyGetCampagneMediaQuery,
  useLazyGetProjectQuery,
  useLazyGetCampagneReqQuery,
  useLazyGetAnnoncesVigueurQuery,
  useLazyGetListingProduitsQuery,
  useLazyGetInventaireQuery,
  useLazyGetUrlQuery,
  useLazyGetUrlWithParamsQuery,
  useLazyGetTachesUsagerQuery,
  useGetInstructionsMutation,
  useAddProjectMutation,
  useAddCampagneReqMutation,
  useAddCampagneDiffuseurMutation,
  useAddDocumentsMetaMutation,
  useAddCampagneNoteMutation,
  useAddCampagneTypeProdMutation,
  useAddAnnonceProdMutation,
  useAddDocumentMutation,
  useAddTacheMutation,
  useAddTacheActiviteMutation,
  useAddTachePlanifMutation,
  useAddTacheUsagerMutation,
  useAddAnnonceMutation,
  useAddAnnonceVigueurMutation,
  usePostUrlMutation,
  useAddProduitAnnonceMutation,
  useUpdateProjectMutation,
  useUpdateAnnoncesVigueurMutation,
  useUpdateCampagneReqMutation,
  useUpdateCampagneDiffuseurMutation,
  useUpdateDocumentsMetaMutation,
  useUpdateCampagneNoteMutation,
  useUpdateCampagneTypeProdMutation,
  useUpdateCampagneTypeProdAfficheMutation,
  useUpdateTachePlanifMutation,
  useUpdateTacheUsagerMutation,
  useUpdateAnnonceMutation,
  usePutUrlMutation,
  useDeleteURLMutation,
  useRemoveProjectMutation,
  useRemoveCampagneReqMutation,
  useRemoveDocumentMutation,
  useRemoveCampagneDiffuseurMutation,
  useRemoveDocumentMetaMutation,
  useRemoveCampagneNoteMutation,
  useRemoveCampagneTypeProdMutation,
  useRemoveAnnonceProdMutation,
  useRemoveTachePlanifMutation,
  useRemoveAnnonceMutation,
  useRemoveAnnonceVigueurMutation,
  useAddCampagneMediaMutation,
  useRemoveCampagneMediaMutation,
} = pubApi;
